<template>
  <div 
    v-if="sortedAndFlattenedGroups.length > 0"
    class="d-flex justify-content-between align-items-center mb-2"
  >
    <div>
      <div v-if="hasDataGroupsMultipleLevels">
        <button class="btn-expandable btn-expandable-sm btn-expandable-add me-2" @click="expandAllRows"><span>Expand All</span></button>
        <button class="btn-expandable btn-expandable-sm btn-expandable-add" @click="collapseAllRows"><span>Collapse All</span></button>
      </div>
    </div>
    <div>
      <span class="text-color-light">{{ selectedDataGroups.label }}s</span> <span v-if="selectedDataGroups.selected.length > 0"><span class="text-color-light">(</span>{{ selectedDataGroups.selected.length }} <span class="text-color-light">selected)</span></span>
    </div>
  </div>
  <div class="table-wrapper">
    <div :class="['table-responsive', { 'spinner-is-visible': sortedAndFlattenedGroups.length <= 0 }]">
      <table 
        v-if="sortedAndFlattenedGroups.length > 0"
        class="table table-striped"
      >
        <thead>
          <tr>
            <th class="align-middle" style="width: 30px">
              <input
                type="checkbox"
                id="select-all"
                class="form-control-checkbox"
                :checked="isAllDataGroupsSelected"
                @change="toggleAllDataGroups"
              />
              <label v-if="sortedAndFlattenedGroups.length > 0" for="select-all">
                <svg width="20" height="20" aria-hidden="true">
                  <use href="#ico-checkbox"></use>
                </svg>
              </label>
            </th>
            <th 
              :class="['vue-sorter', orderClass('name')]"
              @click="reorder('name')"
            >{{ selectedDataGroups.label }}</th>
            <th 
              :class="['vue-sorter', orderClass('approved'), 'align-middle']"
              style="width: 100px"
              @click="reorder('approved')"
            >Approved</th>
            <th
              :class="['text-end', 'vue-sorter', orderClass('totalScope')]"
              style="width: 150px"
              @click="reorder('totalScope')"
            >Total Scope</th>
            <th
              :class="['text-end', 'vue-sorter', orderClass('addPoles')]"
              style="width: 150px"
              @click="reorder('addPoles')"
            >Add Pole</th>
            <th
              :class="['text-end', 'vue-sorter', orderClass('inspections')]"
              style="width: 170px"
              @click="reorder('inspections')"
            >Complete</th>
            <th
              :class="['text-end', 'vue-sorter', orderClass('remaining')]"
              style="width: 150px"
              @click="reorder('remaining')"
            >Remaining</th>
            <th
              :class="['text-end', 'vue-sorter', orderClass('statusesQC')]"
              style="width: 150px"
              @click="reorder('statusesQC')"
            >QC Count</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="group in sortedAndFlattenedGroups"
            :key="group._key"
            :class="'group-level-' + group._depth"
          >
            <td class="group-select">
              <template v-if="group._depth === 0">
                <input
                  type="checkbox"
                  class="form-control-checkbox"
                  :id="'data-group-' + group._key"
                  v-model="selectedDataGroups.groups[group.name]"
                  @change="toggleDataGroup(group.name)"
                  @click.stop
                />
                <label v-if="sortedAndFlattenedGroups.length > 0" :for="'data-group-' + group._key">
                  <svg width="20" height="20" aria-hidden="true">
                    <use href="#ico-checkbox"></use>
                  </svg>
                </label>
              </template>
            </td>
            <td class="group-name">
              <div style="min-height: 14px; white-space: nowrap;">
                <svg
                  v-if="group.dataGroups?.length"
                  @click.stop="toggleRow(group._key)"
                  width="14" 
                  height="14" 
                  viewBox="0 0 16 16" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style="cursor: pointer; user-select: none; margin-right: 7px;"
                >
                  <path d="M1 2C1 1.44772 1.44772 1 2 1H14C14.5523 1 15 1.44772 15 2V14C15 14.5523 14.5523 15 14 15H2C1.44772 15 1 14.5523 1 14V2Z" stroke="#5bc0de" stroke-width="2" stroke-linejoin="round"/>
                  <path d="M5 8H11" stroke="#5bc0de" stroke-width="2" stroke-linecap="round"/>
                  <path v-if="!isRowExpanded(group._key)" d="M8 11L8 5" stroke="#5bc0de" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <template v-if="group.name != 'undefined'">
                {{ group.name }}
                </template>
                <template v-else>
                  <span class="text-color-pale">
                    {{ group.name }}
                  </span>
                </template>
                <span v-if="group._depth > 0" class="group-name-label">({{ group.label }})</span>
              </div>
            </td>
            <td :class="['text-center', { 'spinner-is-visible': group._depth === 0 && group.approved === false && pendingApprovalGroups[group.name] }]">
              <template v-if="group.approved === true">
                <span class="item-status-approved">approved</span>
              </template>
              <template v-else>
                <template v-if="group._depth === 0 && group.approved === false && !pendingApprovalGroups[group.name] && group.name != 'undefined'">
                  <CellAction
                    action="approve"
                    text="Approve"
                    title="Approve Data Group Records"
                    confirm="Are you sure you want to approve all records in this data group? This action cannot be undone."
                    @clickAction="approveDataGroupRecords(group.type, group.name)"
                  />
                </template>
                <template v-if="group._depth === 0 && group.approved === false && pendingApprovalGroups[group.name]">
                  <div class="spinner"></div>
                </template>
              </template>
            </td>
            <td class="text-end">
              {{ group.totalScope }}
            </td>
            <td class="text-end">
              {{ group.addPoles }}
            </td>
            <td class="text-end">
              <template v-if="group._depth === 0">
                <div class="data-group-completeness">
                  <circle-chart
                    css="inline-circle-chart"
                    :width="30"
                    :height="30"
                    :percentage="Math.floor((group.complete / group.totalScope) * 100)"
                    :color="regionsGridChartColor(Math.floor((group.complete / group.totalScope) * 100))"
                    :background-color="regionsGridChartColor(Math.floor((group.complete / group.totalScope) * 100))"
                  />
                  {{ group.complete }}
                </div>
              </template>
              <template v-else>
                {{ group.complete }}
              </template>
            </td>
            <td class="text-end">
              {{ group.remaining }}
            </td>
            <td class="text-end">
              {{ group.inspections > 0 ? ((group.statusesQC / group.inspections) * 100).toFixed(2) + ' %' : '' }}
            </td>
          </tr>
        </tbody>
      </table>
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import CellAction from "../../shared/grid/cell_action";
import RowAction from "../../shared/grid/row_action";

import CircleChart from "../../shared/stats/circle_chart";

import Helpers from "../../../helpers.js";

export default {
  components: {
    RowAction,
    CellAction,
    CircleChart
  },

  props: {
    fulcrumAppId: String,
    dataGroups: {
      type: Array,
      required: true
    },
    policyUpdate: Boolean
  },

  emits: ['dataGroupsSelectionChange', 'dataGroupApproved'],

  data() {
    return {
      expandedKeys: new Set(),
      
      sortAsc: true,
      sortBy: 'name',

      pendingApprovalGroups: {},

      selectedDataGroups: {
        type: null,
        label: null,
        selected: [],
        groups: {}
      }
    };
  },

  setup () {
    return {
      h$: Helpers
    }
  },

  watch: {
    dataGroups: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal || newVal.length == 0) return;

        // Set the selectedDataGroups based on the new dataGroups
        this.selectedDataGroups.groups = newVal.reduce((acc, group) => {
          acc[group.name] = true;
          return acc;
        }, {});

        this.selectedDataGroups.type = newVal[0].type;
        this.selectedDataGroups.label = newVal[0].label;
        this.selectedDataGroups.selected = newVal.map(group => group.name);

        // Emit the initial selection
        this.$emit('dataGroupsSelectionChange', this.selectedDataGroups)
      }
    }
  },

  computed: {
    sortedAndFlattenedGroups() {
    // Step 1: Recursively sort the tree (not the flat list)
      const sortedTree = this.sortBy
        ? this.sortTree(this.dataGroups, this.sortBy, this.sortAsc)
        : this.dataGroups;

      // Step 2: Flatten the sorted tree, preserving hierarchy and expand/collapse
      return this.flattenGroupsTree(sortedTree, 0, '');
    },

    hasDataGroupsMultipleLevels() {
      return this.dataGroups.some(group => (group.dataGroups || []).length > 0);
    },

    isAllDataGroupsSelected() {
      return this.selectedDataGroups.selected.length === this.dataGroups.length;
    }
  },

  methods: {
    sanitizeName(name, index) {
      let groupName = name || `group ${index + 1}`;
      
      return groupName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
        .replace(/\s+/g, '')          // Remove spaces
        .replace(/-+/g, '-')          // Collapse multiple hyphens
        .replace(/^-+|-+$/g, '')      // Remove leading/trailing hyphens
        .trim();
    },

    flattenGroupsTree(groups, depth = 0, prefix = '') {
      return groups.flatMap((group, index) => {
        const groupName = this.sanitizeName(String(group.name), index);
        const key = `${prefix}${groupName}-${depth}-${index}`;        
        const row = { ...group, _depth: depth, _key: key };
        const children = group.dataGroups || [];

        const result = [row];

        if (this.expandedKeys.has(key) && children.length > 0) {
          result.push(...this.flattenGroupsTree(children, depth + 1, key + '_'));
        }

        return result
      });
    },

    sortTree(groups, field, asc = true) {
      const sorted = [...groups].sort((a, b) => {
        const aVal = a[field] ?? '';
        const bVal = b[field] ?? '';
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return asc ? aVal - bVal : bVal - aVal;
        } else {
          return asc
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        }
      });

      return sorted.map(group => {
        const children = group.dataGroups?.length
          ? this.sortTree(group.dataGroups, field, asc)
          : [];
        return {
          ...group,
          dataGroups: children
        };
      });
    },

    regionsGridChartColor(n) {
      let color = this.h$.damageColors[5]

      if (n > 15) color = this.h$.damageColors[4]
      if (n > 75) color = this.h$.damageColors[3]
      if (n == 100) color = this.h$.damageColors[1]

      return color
    },

    toggleRow(key) {      
      if (this.expandedKeys.has(key)) {
        this.expandedKeys.delete(key);
      } else {
        this.expandedKeys.add(key);
      }
    },

    isRowExpanded(key) {
      return this.expandedKeys.has(key);
    },

    expandAllRows() {
      this.collapseAllRows();

      const keys = new Set();

      const collectAllKeys = (groups, depth = 0, prefix = '') => {
        groups.forEach((group, index) => {
          const groupName = this.sanitizeName(String(group.name), index);
          const key = `${prefix}${groupName}-${depth}-${index}`;

          keys.add(key);

          const children = group.dataGroups || [];

          if (children.length > 0) {
            collectAllKeys(children, depth + 1, key + '_');
          }
        });
      };

      collectAllKeys(this.sortedAndFlattenedGroups);

      this.expandedKeys = keys;
    },

    collapseAllRows() {
      this.expandedKeys = new Set();
    },

    reorder(field) {
      this.collapseAllRows();

      if (this.sortBy === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortBy = field;
        this.sortAsc = true;
      }
    },

    orderClass(field) {
      if (this.sortBy == field) {
        return 'sort-order-' + (this.sortAsc ? 'asc' : 'desc')
      }
    },

    isInSelectedDataGroups: function(groupName) {
      return this.selectedDataGroups.selected.includes(groupName);
    },

    toggleDataGroup: function(groupName) {
      if (this.isInSelectedDataGroups(groupName)) {
        this.selectedDataGroups.selected = this.selectedDataGroups.selected.filter(r => r !== groupName);
      } else {
        this.selectedDataGroups.selected.push(groupName);
      }

      setTimeout(() => {
        this.$emit('dataGroupsSelectionChange', this.selectedDataGroups)
      }, 400);
    },

    toggleAllDataGroups(event) {
      const checked = event.target.checked;
      
      const topGroups = this.sortedAndFlattenedGroups.filter(g => g._depth === 0);

      if (checked) {
        this.selectedDataGroups.selected = topGroups.map(g => g.name);
      } else {
        this.selectedDataGroups.selected = [];
      }

      topGroups.forEach(g => {
        this.selectedDataGroups.groups[g.name] = checked;
      });

      this.$nextTick(() => {
        this.$emit('dataGroupsSelectionChange', this.selectedDataGroups)
      });
    },

    approveDataGroupRecords: function(groupType, groupName) {
      const topGroups = this.dataGroups;
      
      const groupToApprove = topGroups.find(g => g.name === groupName);

      if (!groupToApprove) {
        app.ui.toast.add({
          priority: "error",
          title: "Group Not Found",
          message: `Unable to find the data group "${groupName}" for approval. Please try again.`
        });
        return;
      }

      // Show spinner in Approved grid column cell while approval is in progress
      this.pendingApprovalGroups = {
        ...this.pendingApprovalGroups,
        [groupName]: true
      };

      // Approve records for the group via GraphQL mutation
      app.graphql.get(
        "update.fulcrumApp.approveRecords",

        {
          filter: {
            filter: {
              fulcrumAppId: [this.fulcrumAppId],
              data: [{
                dataName: groupType,
                values: [groupName]
              }]
            }
          }
        },

        (data) => {
          const response = data.data.approveFulcrumRecords;

          if (response.errors) {
            response.errors.forEach((error) => {
              app.ui.toast.add({
                priority: "warning",
                title: "Unable to approve records",
                message: error
              });
            })

            // Hide spinner
            this.pendingApprovalGroups = {
              ...this.pendingApprovalGroups,
              [groupName]: false
            };
          } else {
            setTimeout(() => {
              app.ui.toast.add({
                priority: "success",
                title: "Group Approved",
                message: `All records in the "${groupName}" data group have been approved.`
              });

              // Hide spinner
              this.pendingApprovalGroups = {
                ...this.pendingApprovalGroups,
                [groupName]: false
              };    
            }, 1000);

            // Emit to the Dashboard that group records are approved
            this.$emit('dataGroupApproved', { groupType, groupName });
          }
        }
      )
    }
  }
};
</script>
